import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

type SidebarSectionKey = string;

type SidebarContextValue = {
  isCollapsed: boolean;
  toggleCollapsed: () => void;
  setCollapsed: (collapsed: boolean) => void;
  expandedSections: Record<SidebarSectionKey, boolean>;
  toggleSection: (key: SidebarSectionKey) => void;
  setSectionExpanded: (key: SidebarSectionKey, expanded: boolean) => void;
  activePath: string;
  setActivePath: React.Dispatch<React.SetStateAction<string>>;
};

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<SidebarSectionKey, boolean>>({ payroll: false });
  const [activePath, setActivePath] = useState(location.pathname);

  useEffect(() => {
    setActivePath(location.pathname);
  }, [location.pathname]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((v) => !v);
  }, []);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
  }, []);

  const toggleSection = useCallback((key: SidebarSectionKey) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setSectionExpanded = useCallback((key: SidebarSectionKey, expanded: boolean) => {
    setExpandedSections((prev) => ({ ...prev, [key]: expanded }));
  }, []);

  const value = useMemo(
    () => ({
      isCollapsed,
      toggleCollapsed,
      setCollapsed,
      expandedSections,
      toggleSection,
      setSectionExpanded,
      activePath,
      setActivePath,
    }),
    [activePath, expandedSections, isCollapsed, setCollapsed, setSectionExpanded, toggleCollapsed, toggleSection]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return ctx;
}
